import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { getLoanServicingStatus, InputType, IVisibleLogic } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { useBankNameLookup } from 'hooks/useBankNameLookup'
import { requestedToParties } from 'pages/LoanSubmission/constants'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, getAdminEmails, setAdminConfig } from 'services'
import { ButtonGroup } from 'stories/components'
import { InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { RestructureLoanRelease } from '../RestructureLoanRelease'
import { ApplicationPdf } from './ApplicationPdf'
import { ConditionClearRules, IConditionClearRules, normalizeConditionClearRules } from './ConditionClearRules'
import { getTabConfigs, notificationConfigKeys, paymentProviderOptions } from './constants'
import {
  LoanApplicationDateRules,
  normalizeLoanApplicationDateRules,
  TLoanApplicationDateRulesConfig,
} from './LoanApplicationDateRules'
import { OverviewStepTitles } from './OverviewStepTitles'
import { IPaymentMethodsByArea, PaymentMethodsByArea, paymentMethodsByAreaKeys } from './PaymentMethodsByArea'
import { SignDisclosuresModal } from './SignDisclosuresModal'
import { TextMessageInviteAll } from './TextMessageInviteAll'

const loanApplicationSubTabs = {
  settings: 'Settings',
  applicationPdf: 'Application PDF',
  signDisclosuresModal: 'Sign Disclosures Modal',
  overviewStepTitles: 'Overview Step Titles',
}

export const RulesConfiguration = () => {
  const { loanGlobalStatus, vendor } = useSelector((state: any) => ({
    loanGlobalStatus: state.loanGlobalStatus,
    vendor: state.vendor,
  }))

  const loanServicingStatus = getLoanServicingStatus(true)

  const tabConfigs = useMemo(
    () => getTabConfigs(loanGlobalStatus, loanServicingStatus, vendor),
    [loanGlobalStatus, vendor],
  )

  const tabTitles = useMemo(() => tabConfigs.map((tab) => tab.title), [tabConfigs])

  const [inputs, setInputs] = useState<Record<string, Record<string, InputType>>>({})
  const { resolveBankName } = useBankNameLookup()
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<Record<string, any>>({})
  const [adminEmails, setAdminEmails] = useState<string[]>([])
  const [partyTab, setPartyTab] = useState('Borrower')
  const [activeTab, setActiveTab] = useState(tabConfigs[0]?.title || '')
  const [loanAppSubTab, setLoanAppSubTab] = useState('settings')
  const [loanSubStatusConfig, setLoanSubStatusConfig] = useState<Record<string, any[]>>({})

  const loanSubStatusOptions = useMemo(() => {
    const options: Record<string, string> = {}
    Object.keys(loanSubStatusConfig).forEach((statusKey) => {
      ;(loanSubStatusConfig[statusKey] || []).forEach((sub: any) => {
        const statusLabel = loanGlobalStatus[statusKey] || statusKey
        options[`${statusKey}:${sub.id}`] = `${statusLabel} - ${sub.title}`
      })
    })
    return options
  }, [loanSubStatusConfig, loanGlobalStatus])

  useEffect(() => {
    const initialInputs: Record<string, Record<string, InputType>> = {}
    tabConfigs.forEach((tab) => {
      initialInputs[tab.key] = tab.fields(loanGlobalStatus)
    })
    setInputs(initialInputs)

    getAdminConfig('rules')
      .then((rulesConfig) => {
        const normalizedRules = {
          ...rulesConfig,
          applyTridRules: rulesConfig.applyTridRules ?? rulesConfig.tridRules?.enabled ?? false,
          tridRulesVisibleLogic: rulesConfig.tridRulesVisibleLogic ?? rulesConfig.tridRules?.visibleLogic ?? [],
          loanApplicationDateRules: normalizeLoanApplicationDateRules(rulesConfig),
          conditionClearRules: normalizeConditionClearRules(rulesConfig),
        }
        setData(normalizedRules)
        const newInputs: Record<string, Record<string, InputType>> = {}
        tabConfigs.forEach((tab) => {
          const tabFields = tab.fields(loanGlobalStatus)
          Object.keys(tabFields).forEach((key) => {
            if (normalizedRules[key] !== undefined && normalizedRules[key] !== null) {
              tabFields[key].value = normalizedRules[key]
            }
          })
          newInputs[tab.key] = tabFields
        })
        setInputs(newInputs)
      })
      .finally(() => setLoading(false))

    getAdminEmails().then((emails: string[]) => setAdminEmails(emails))
    getAdminConfig('loanSubStatus').then((value) => setLoanSubStatusConfig(value || {}))
  }, [tabConfigs])

  const assigneeOptions = useMemo(() => {
    const options = { ...accountTypes }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails])

  const activeTabConfig = useMemo(() => tabConfigs.find((tab) => tab.title === activeTab), [tabConfigs, activeTab])

  const buildPayload = (
    inputsToSave: Record<string, Record<string, InputType>>,
    notificationData: Record<string, any>,
  ) => {
    const json: Record<string, any> = {}

    Object.keys(inputsToSave).forEach((tabKey) => {
      const tabInputs = inputsToSave[tabKey]
      Object.keys(tabInputs).forEach((key) => {
        if (tabInputs[key].inputType === 'section') return
        json[key] = tabInputs[key].value
      })
    })

    json.notification = notificationData.notification
    notificationConfigKeys.forEach((key) => delete json[key])

    if (notificationData.loanApplicationDateRules) {
      json.loanApplicationDateRules = notificationData.loanApplicationDateRules
    }
    if (notificationData.conditionClearRules) {
      json.conditionClearRules = notificationData.conditionClearRules
    }
    if (notificationData.tridRulesVisibleLogic) {
      json.tridRulesVisibleLogic = notificationData.tridRulesVisibleLogic
    }
    // Saving replaces the whole rules blob, so the grid's keys have to ride along on every save.
    paymentMethodsByAreaKeys.forEach((key) => {
      if (notificationData[key] !== undefined) json[key] = notificationData[key]
    })

    return json
  }

  const onChange = (key: string, value: any) => {
    if (!activeTabConfig) return

    let notificationData = data
    if (notificationConfigKeys.includes(key)) {
      const temp = cloneDeep(data)
      if (!temp.notification) temp.notification = {}
      if (!temp.notification[partyTab]) temp.notification[partyTab] = {}
      temp.notification[partyTab][key] = value
      setData(temp)
      notificationData = temp
    }

    const newInputs = cloneDeep(inputs)
    if (newInputs[activeTabConfig.key]?.[key]) {
      newInputs[activeTabConfig.key][key].value = InputConvert(newInputs[activeTabConfig.key][key], value)
      newInputs[activeTabConfig.key][key].error = ''
      setInputs(newInputs)
      submitConfig(newInputs, notificationData)
      if (key === 'payoffWireRoutingNumber')
        resolveBankName(value, newInputs[activeTabConfig.key].payoffWireBankName?.value).then((bankName) => {
          if (!bankName) return
          const filled = cloneDeep(newInputs)
          filled[activeTabConfig.key].payoffWireBankName.value = bankName
          setInputs(filled)
          submitConfig(filled, notificationData)
        })
    }
  }

  const submitConfig = async (
    inputsToSave: Record<string, Record<string, InputType>> = inputs,
    notificationData: Record<string, any> = data,
  ) => {
    setLoading(true)
    await setAdminConfig('rules', buildPayload(inputsToSave, notificationData))
    setLoading(false)
  }

  const onLoanApplicationDateRulesChange = (value: TLoanApplicationDateRulesConfig) => {
    const temp = cloneDeep(data)
    temp.loanApplicationDateRules = value
    setData(temp)
    submitConfig(inputs, temp)
  }

  const onConditionClearRulesChange = (value: IConditionClearRules) => {
    const temp = cloneDeep(data)
    temp.conditionClearRules = value
    setData(temp)
    submitConfig(inputs, temp)
  }

  const onTridRulesVisibleLogicChange = (visibleLogic: IVisibleLogic[][]) => {
    const temp = cloneDeep(data)
    temp.tridRulesVisibleLogic = visibleLogic
    setData(temp)
    submitConfig(inputs, temp)
  }

  const onPaymentMethodsByAreaChange = (key: keyof IPaymentMethodsByArea, value: Record<string, any>) => {
    const temp = cloneDeep(data)
    temp[key] = value
    setData(temp)
    submitConfig(inputs, temp)
  }

  const renderLoanApplicationSubTab = () => {
    switch (loanAppSubTab) {
      case 'applicationPdf':
        return <ApplicationPdf />
      case 'signDisclosuresModal':
        return <SignDisclosuresModal />
      case 'overviewStepTitles':
        return <OverviewStepTitles />
      case 'settings':
      default:
        return (
          <>
            {renderTabFields()}
            <LoanApplicationDateRules
              value={data.loanApplicationDateRules}
              loanGlobalStatus={loanGlobalStatus}
              loanSubStatusOptions={loanSubStatusOptions}
              onChange={onLoanApplicationDateRulesChange}
              applyTridRules={!!inputs.loanApplication?.applyTridRules?.value}
              onApplyTridRulesChange={(enabled) => onChange('applyTridRules', enabled)}
              tridRulesVisibleLogic={data.tridRulesVisibleLogic}
              onTridRulesVisibleLogicChange={onTridRulesVisibleLogicChange}
            />
          </>
        )
    }
  }

  const renderLoanApplicationTab = () => {
    return (
      <div>
        <div className="mb-4">
          <ButtonGroup title={loanApplicationSubTabs} value={loanAppSubTab} onChange={setLoanAppSubTab} />
        </div>

        {renderLoanApplicationSubTab()}
      </div>
    )
  }
  const onSubmit = () => submitConfig()

  const renderTabContent = () => {
    if (!activeTabConfig) return null

    if (activeTabConfig.key === 'loanApplication') return renderLoanApplicationTab()
    if (activeTabConfig.key === 'restructureLoanRelease') return <RestructureLoanRelease />
    if (activeTabConfig.key === 'loanSubmissionNotification') {
      return (
        <>
          {renderTabFields()}
          <ConditionClearRules value={data.conditionClearRules} onChange={onConditionClearRulesChange} />
        </>
      )
    }

    if (activeTabConfig.key === 'paymentMethods') {
      return (
        <>
          {renderTabFields()}
          <PaymentMethodsByArea
            value={data}
            providerOptions={paymentProviderOptions(vendor)}
            defaultProvider={inputs.paymentMethods?.paymentProvider?.value}
            onChange={onPaymentMethodsByAreaChange}
          />
        </>
      )
    }

    return renderTabFields()
  }

  const renderTabFields = () => {
    if (!activeTabConfig) return null

    const tabInputs = inputs[activeTabConfig.key]
    if (!tabInputs) return null

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {Object.keys(tabInputs).map((key, index) => {
          const item = cloneDeep(tabInputs[key])

          if (notificationConfigKeys.includes(key)) {
            const stored = data?.notification?.[partyTab]?.[key]
            item.value = stored === undefined && key === 'autoNotifyOnHoldTransition' ? false : stored
          }

          if (key === 'emailWithdrawnLoans') {
            ;(item as any).options = assigneeOptions
          }

          // Default notification profiles only apply to Third Party Originator (Broker)
          if (key === 'defaultNotifyParties' && partyTab !== 'Broker') {
            return null
          }

          if (key === 'autoNotifyOnHoldTransition' && partyTab !== 'Borrower') {
            return null
          }

          const elements = [
            <div className={`input md:col-span-${item.span || 1}`} key={index}>
              <RenderInput input={item} Key={key} onChange={onChange} onBlur={onSubmit} />
            </div>,
          ]

          if (key === 'borrowerTextingRequireConsent') {
            elements.push(
              <div className="col-span-full" key={`invite-${index}`}>
                <TextMessageInviteAll />
              </div>,
            )
          }

          if (key === 'unClearWhenNewDocument') {
            elements.push(
              <div className="col-span-full" key={`party-${index}`}>
                <ButtonGroup title={requestedToParties} value={partyTab} onChange={setPartyTab} />
              </div>,
            )
          }

          return elements
        })}
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />

      <div className="mb-4">
        <ButtonGroup title={tabTitles} value={activeTab} onChange={setActiveTab} />
      </div>

      {renderTabContent()}
    </div>
  )
}
